<template>
  <div class="pm-wrap">
    <p class="pm-intro">
      Carriers resolve to a Stedi payer automatically when Open Dental has an Electronic ID
      or a global default exists. Anything left <strong>Unmapped</strong> can't be verified until
      you add a payer ID below.
    </p>

    <div class="pm-toolbar">
      <input
        v-model="search"
        type="text"
        class="pm-search"
        placeholder="Search carriers…"
        aria-label="Search carriers"
      />
      <label class="pm-checkline">
        <input v-model="unmappedOnly" type="checkbox" />
        <span>Unmapped only</span>
        <span v-if="unmappedCount" class="pm-count">{{ unmappedCount }}</span>
      </label>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="pm-state">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading carriers…
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pm-state pm-state--error">
      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
      <span>{{ error }}</span>
      <button type="button" class="pm-retry" @click="load(true)">Retry</button>
    </div>

    <!-- Empty -->
    <div v-else-if="!rows.length" class="pm-state">
      {{ carriers.length ? 'No carriers match your search.' : 'No carriers found for this practice.' }}
    </div>

    <!-- Rows -->
    <div v-else class="pm-list">
      <div v-for="row in rows" :key="row.carrierNum" class="pm-row" :class="{ 'is-unmapped': row.isUnmapped }">
        <div class="pm-row-main">
          <div class="pm-carrier">{{ row.carrierName }}</div>
          <div class="pm-statusline">
            <span v-if="row.isUnmapped" class="pm-chip pm-chip--unmapped">
              <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> Unmapped
            </span>
            <span v-else class="pm-chip pm-chip--mapped">
              <i class="fa fa-check" aria-hidden="true"></i> {{ row.sourceLabel }}
            </span>
            <span v-if="row.resolvedPayerId" class="pm-resolved">
              Payer <span class="pm-mono">{{ row.resolvedPayerId }}</span>
            </span>
            <span v-if="isExcluded(row.carrierNum)" class="pm-chip pm-chip--excluded">
              <i class="fa fa-ban" aria-hidden="true"></i> Excluded
            </span>
          </div>

          <!--
            Never check this carrier electronically. For carriers that ARE reachable and
            mapped but whose 271 carries no usable benefits — network/capitation products
            like Cigna PPO Access Plus, which answer with no maximum, no deductible and no
            orthodontic allowance. That check completes and BILLS, so this is a pure saving.

            ⛔ Saved as a ONE-KEY PATCH to /save-settings, not the whole blob. The Automation
            tab edits the same blob and may have unsaved changes sitting in it; the backend
            merges defaults ⊕ stored ⊕ patch, so sending only this key cannot clobber them.
          -->
          <label class="pm-exclude">
            <span class="iv-switch" :class="{ 'iv-switch--disabled': excludeSaving }">
              <input
                type="checkbox"
                :checked="isExcluded(row.carrierNum)"
                :disabled="excludeSaving"
                @change="onToggleExclude(row, $event.target.checked)"
              />
              <span class="iv-switch-slider"></span>
            </span>
            <span class="pm-exclude-label">Exclude from electronic verification</span>
          </label>
          <div v-if="rowMsg[row.carrierNum]" class="pm-rowmsg" :class="`is-${rowMsg[row.carrierNum].type}`">
            {{ rowMsg[row.carrierNum].text }}
          </div>
        </div>
        <div class="pm-row-edit">
          <input
            v-model="payerInput[row.carrierNum]"
            type="text"
            class="pm-payer-input"
            :placeholder="row.resolvedPayerId ? row.resolvedPayerId : 'Payer ID'"
            :aria-label="`Payer ID for ${row.carrierName}`"
            @keyup.enter="onSave(row)"
          />
          <button
            type="button"
            class="pm-save"
            :disabled="!canSave(row) || !!savingByCarrier[row.carrierNum]"
            @click="onSave(row)"
          >
            <i v-if="savingByCarrier[row.carrierNum]" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions } from 'vuex';

function present(v) {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

export default {
  name: 'PayerMappingList',
  props: {
    siteId: { type: String, default: '' },
    // The parent modal only mounts/reveals this when the Payer mapping tab is
    // active; `active` flips true then so we lazy-load on first reveal.
    active: { type: Boolean, default: false },
  },

  data() {
    return {
      carriers: [],
      mappings: [],
      loading: false,
      error: null,
      loaded: false,
      search: '',
      unmappedOnly: false,
      // CarrierNums this office excludes from electronic verification, mirrored from the
      // settings blob. Saved back as a ONE-KEY patch — see onToggleExclude.
      excludedCarrierNums: [],
      excludeSaving: false,
      // Per-carrier editable payer-ID input + save state, keyed by CarrierNum.
      payerInput: {},
      // What payerInput looked like when it was last seeded or saved. Only used to answer
      // "does this tab hold unsaved work?" for the parent modal's close guard — rows here
      // save individually, so a typed-but-unsaved id is invisible otherwise.
      baselineInput: {},
      savingByCarrier: {},
      rowMsg: {},
    };
  },

  computed: {
    // Every carrier resolved to its mapping state (mirrors the backend
    // dashboard-assemble unmapped detection: ElectID → site override → global).
    resolvedRows() {
      const siteOverrides = new Map(); // CarrierNum -> row
      const globals = new Map();       // CarrierNameNorm -> row
      for (const m of this.mappings) {
        if (!m) continue;
        const lob = present(m.LineOfBusiness) ? String(m.LineOfBusiness) : 'dental';
        if (lob !== 'dental') continue; // the admin screen manages the dental LOB
        if (present(m.SiteID)) {
          if (present(m.CarrierNum)) siteOverrides.set(String(m.CarrierNum), m);
        } else if (present(m.CarrierNameNorm)) {
          globals.set(String(m.CarrierNameNorm), m);
        }
      }

      return this.carriers.map((c) => {
        const carrierNum = c.CarrierNum;
        const carrierName = present(c.CarrierName) ? String(c.CarrierName) : `Carrier ${carrierNum}`;
        const nameNorm = String(carrierName).trim().toUpperCase();
        const override = siteOverrides.get(String(carrierNum)) || null;
        const global = globals.get(nameNorm) || null;
        const hasElectId = present(c.ElectID);

        let sourceLabel = '';
        let resolvedPayerId = null;
        if (override) {
          sourceLabel = 'Mapped (manual)';
          resolvedPayerId = present(override.PayerID) ? String(override.PayerID) : null;
        } else if (global) {
          sourceLabel = 'Mapped (default)';
          resolvedPayerId = present(global.PayerID) ? String(global.PayerID) : null;
        } else if (hasElectId) {
          sourceLabel = 'Mapped (OD ElectID)';
          resolvedPayerId = String(c.ElectID).trim();
        }

        return {
          carrierNum,
          carrierName,
          isUnmapped: !override && !global && !hasElectId,
          sourceLabel,
          resolvedPayerId,
        };
      });
    },

    unmappedCount() {
      return this.resolvedRows.filter((r) => r.isUnmapped).length;
    },

    rows() {
      const q = (this.search || '').trim().toLowerCase();
      let list = this.resolvedRows;
      if (this.unmappedOnly) list = list.filter((r) => r.isUnmapped);
      if (q) list = list.filter((r) => r.carrierName.toLowerCase().indexOf(q) !== -1);
      // Unmapped first, then alphabetical.
      return list.slice().sort((a, b) => {
        if (a.isUnmapped !== b.isUnmapped) return a.isUnmapped ? -1 : 1;
        return a.carrierName.localeCompare(b.carrierName);
      });
    },
  },

  watch: {
    active(now) {
      if (now && !this.loaded && !this.loading) this.load();
    },
    siteId() {
      // Site changed under us — drop the cache so the next reveal reloads.
      this.loaded = false;
      this.carriers = [];
      this.mappings = [];
    },
  },

  mounted() {
    if (this.active && !this.loaded) this.load();
  },

  methods: {
    ...mapActions('insuranceVerification', ['fetchPayerMapping', 'savePayerMapping', 'fetchSettings', 'saveSettings']),

    isExcluded(carrierNum) {
      return this.excludedCarrierNums.indexOf(Number(carrierNum)) !== -1;
    },

    /**
     * Toggle one carrier's exclusion from electronic verification.
     *
     * ⛔ SENDS ONLY THIS KEY. /save-settings merges defaults ⊕ stored ⊕ patch, so a one-key
     * patch leaves every other setting alone — including unsaved edits sitting in the
     * Automation tab's form, which shares this same blob. Sending the whole blob from here
     * would silently persist or discard work the user has not saved yet.
     *
     * Optimistic, reverted on failure: the toggle must feel instant, but it decides whether
     * real money is spent, so it must never LOOK excluded while the server still thinks
     * otherwise.
     */
    async onToggleExclude(row, next) {
      if (this.excludeSaving) return;
      const num = Number(row.carrierNum);
      if (!Number.isFinite(num) || num <= 0) return;
      const before = this.excludedCarrierNums.slice();
      const after = next
        ? before.concat([num]).filter((v, i, a) => a.indexOf(v) === i).sort((x, y) => x - y)
        : before.filter((v) => v !== num);
      this.excludedCarrierNums = after;
      this.excludeSaving = true;
      try {
        await this.saveSettings({ siteID: this.siteId, settings: { excludedCarrierNums: after } });
        this.$set(this.rowMsg, row.carrierNum, {
          type: 'ok',
          text: next ? 'Excluded — this carrier will not be checked electronically.' : 'No longer excluded.',
        });
      } catch (err) {
        this.excludedCarrierNums = before;
        this.$set(this.rowMsg, row.carrierNum, {
          type: 'err',
          text: (err && err.message) || 'Could not save the exclusion',
        });
      } finally {
        this.excludeSaving = false;
      }
    },

    canSave(row) {
      return present(this.payerInput[row.carrierNum]);
    },

    async load(force = false) {
      if (!this.siteId) return;
      if (this.loaded && !force) return;
      this.loading = true;
      this.error = null;
      try {
        const data = await this.fetchPayerMapping({ siteID: this.siteId });
        this.carriers = Array.isArray(data.carriers) ? data.carriers : [];
        this.mappings = Array.isArray(data.mappings) ? data.mappings : [];
        this.seedInputs();
        // The exclude list lives in the settings blob, not the payer-map payload, so it is a
        // second read. The store caches it and this tab is lazy-loaded, so it is cheap.
        try {
          const st = await this.fetchSettings({ siteID: this.siteId });
          const list = st && st.settings && st.settings.excludedCarrierNums;
          this.excludedCarrierNums = Array.isArray(list)
            ? list.map(Number).filter((n) => Number.isFinite(n) && n > 0)
            : [];
        } catch (e) {
          // A settings read failure must not blank the carrier list — the mapping half of
          // this tab still works. Exclusions render as off until the next load.
          this.excludedCarrierNums = [];
        }
        this.loaded = true;
      } catch (err) {
        this.error = (err && err.message) || 'Failed to load carriers';
      } finally {
        this.loading = false;
      }
    },

    // Pre-create a reactive input key for every carrier (Vue 2 won't track keys
    // added lazily), seeded with the site override's PayerID when one exists.
    seedInputs() {
      const override = new Map();
      for (const m of this.mappings) {
        if (m && present(m.SiteID) && present(m.CarrierNum)) {
          override.set(String(m.CarrierNum), present(m.PayerID) ? String(m.PayerID) : '');
        }
      }
      const inputs = {};
      for (const c of this.carriers) {
        inputs[c.CarrierNum] = override.get(String(c.CarrierNum)) || '';
      }
      this.payerInput = inputs;
      this.baselineInput = Object.assign({}, inputs);
    },

    /**
     * Does this tab hold a typed-but-unsaved payer ID? Called by the parent modal before it
     * closes — payer rows save one at a time, so without this a half-typed mapping would
     * disappear on close with no warning, which is the same silent-loss the settings tab's
     * close guard exists to prevent.
     */
    hasUnsavedEdits() {
      return Object.keys(this.payerInput).some((k) => {
        const now = (this.payerInput[k] || '').trim();
        const was = (this.baselineInput[k] || '').trim();
        return now !== was;
      });
    },

    async onSave(row) {
      const payerId = this.payerInput[row.carrierNum];
      if (!present(payerId) || this.savingByCarrier[row.carrierNum]) return;
      this.$set(this.savingByCarrier, row.carrierNum, true);
      this.$set(this.rowMsg, row.carrierNum, null);
      try {
        const data = await this.savePayerMapping({
          siteID: this.siteId,
          carrierNum: row.carrierNum,
          carrierName: row.carrierName,
          payerId,
        });
        // Reflect the new site override locally so the row flips to Mapped without
        // a full reload.
        const saved = (data && data.mapping) || {
          SiteID: this.siteId,
          CarrierNum: row.carrierNum,
          CarrierNameNorm: String(row.carrierName).trim().toUpperCase(),
          LineOfBusiness: 'dental',
          PayerID: String(payerId).trim(),
          Source: 'manual',
        };
        this.mergeSavedMapping(saved);
        // This row is now persisted, so it must stop counting as unsaved work.
        this.$set(this.baselineInput, row.carrierNum, String(payerId).trim());
        this.$set(this.rowMsg, row.carrierNum, { type: 'ok', text: 'Saved' });
        if (this.$toast) this.$toast.success(`Payer mapping saved for ${row.carrierName}`);
      } catch (err) {
        const msg = (err && err.message) || 'Failed to save';
        this.$set(this.rowMsg, row.carrierNum, { type: 'err', text: msg });
        if (this.$toast) this.$toast.error(msg);
      } finally {
        this.$set(this.savingByCarrier, row.carrierNum, false);
      }
    },

    // Replace (or add) the site-override row for this carrier in the local list.
    mergeSavedMapping(saved) {
      const idx = this.mappings.findIndex(
        (m) => m && present(m.SiteID) && String(m.CarrierNum) === String(saved.CarrierNum),
      );
      if (idx === -1) this.mappings.push(saved);
      else this.mappings.splice(idx, 1, saved);
    },
  },
};
</script>

<style lang="scss" scoped>
.pm-wrap {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pm-intro {
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #717977;

  strong { color: #a5292e; font-weight: 700; }
}

.pm-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.pm-search {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #c8cfcd;
  border-radius: 9px;
  font-size: 13.5px;
  color: #0c0f0e;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #0f7259;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }
}

.pm-checkline {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: #545b59;
  cursor: pointer;
  white-space: nowrap;
  margin: 0;

  input { cursor: pointer; }
}

.pm-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #fdeeee;
  color: #a5292e;
  font-size: 11px;
  font-weight: 800;
}

.pm-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 26px 8px;
  color: #717977;
  font-size: 13.5px;
  justify-content: center;

  &--error { color: #a5292e; }
}

.pm-retry {
  margin-left: 8px;
  border: 1px solid #e5484d;
  background: #fff;
  color: #a5292e;
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.pm-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #dfe4e3;
  border-radius: 12px;
  background: #ffffff;

  &.is-unmapped {
    border-color: #f8d4d5;
    background: #fffafa;
  }
}

.pm-row-main {
  flex: 1;
  min-width: 0;
}

.pm-carrier {
  font-weight: 700;
  font-size: 14px;
  color: #0c0f0e;
  word-break: break-word;
}

.pm-statusline {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 5px;
  flex-wrap: wrap;
}

.pm-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;

  &--mapped { background: #e9f6f1; color: #0b5c49; }
  &--unmapped { background: #fdeeee; color: #a5292e; }
  // Excluded is a deliberate office choice, not a problem — muted grey, never the amber/red
  // the unmapped chip uses. Scanning this list, "I chose this" must not look like "something
  // is wrong".
  &--excluded { background: #f1f4f3; color: #64748b; }
}

.pm-exclude {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  cursor: pointer;
  user-select: none;
}

.pm-exclude-label {
  font-size: 12px;
  color: #64748b;
}

// The modal's switch, reused. ⛔ The <input> must stay the first child immediately before
// .iv-switch-slider — the checked state is a sibling selector, so reordering breaks it
// silently.
.iv-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  flex: none;
}
.iv-switch input[type="checkbox"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}
.iv-switch-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.16s ease;
  cursor: pointer;
}
.iv-switch-slider::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border-radius: 50%;
  transition: transform 0.16s ease;
}
.iv-switch input:checked + .iv-switch-slider { background: #0f7259; }
.iv-switch input:checked + .iv-switch-slider::before { transform: translateX(16px); }
.iv-switch--disabled { opacity: 0.6; }
.iv-switch--disabled input[type="checkbox"] { cursor: not-allowed; }

.pm-resolved {
  font-size: 12px;
  color: #717977;
}

.pm-mono {
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
  color: #0c0f0e;
  font-weight: 600;
}

.pm-rowmsg {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;

  &.is-ok { color: #0f7259; }
  &.is-err { color: #a5292e; }
}

.pm-row-edit {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

.pm-payer-input {
  width: 120px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #c8cfcd;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
  color: #0c0f0e;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #0f7259;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }
}

.pm-save {
  height: 34px;
  min-width: 58px;
  padding: 0 12px;
  border: 1px solid #0f7259;
  background: #0f7259;
  color: #ffffff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:not(:disabled):hover { background: #0b5c49; border-color: #0b5c49; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
</style>
